/**
 * Welcome Page Pricing Utilities
 *
 * Pure functions for resolving package display on the welcome page.
 * Extracted for testability — no Supabase or DOM dependencies.
 */

export interface PackageDisplay {
  name: string;
  price_cents: number;
  trial_days: number;
  billing_interval: string;
  interval_count?: number;
  stripe_price_id: string;
  class_rate_cents?: number | null;
}

export interface TokenDataWithPackage {
  assigned_package_name: string | null;
  assigned_package_price_cents: number | null;
  assigned_package_trial_days: number | null;
  assigned_package_billing_interval: string | null;
  assigned_package_interval_count?: number | null;
  assigned_package_stripe_price_id: string | null;
  assigned_package_class_rate_cents?: number | null;
}

/**
 * Resolve which package to display on the welcome page.
 *
 * Returns the assigned package if the admin set one, otherwise null
 * (caller should fetch the default package from the database).
 */
export function resolvePackageDisplay(tokenData: TokenDataWithPackage): PackageDisplay | null {
  if (
    tokenData.assigned_package_stripe_price_id &&
    tokenData.assigned_package_name &&
    tokenData.assigned_package_price_cents != null &&
    tokenData.assigned_package_trial_days != null &&
    tokenData.assigned_package_billing_interval
  ) {
    return {
      name: tokenData.assigned_package_name,
      price_cents: tokenData.assigned_package_price_cents,
      trial_days: tokenData.assigned_package_trial_days,
      billing_interval: tokenData.assigned_package_billing_interval,
      interval_count: tokenData.assigned_package_interval_count ?? 1,
      stripe_price_id: tokenData.assigned_package_stripe_price_id,
      class_rate_cents: tokenData.assigned_package_class_rate_cents ?? null,
    };
  }
  return null;
}

/** Format cents as a dollar string. Sub-dollar and fractional amounts keep 2 decimals. */
export function formatCurrency(cents: number): string {
  const dollars = cents / 100;
  const needsDecimals = cents > 0 && (dollars < 1 || dollars % 1 !== 0);
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: needsDecimals ? 2 : 0,
    maximumFractionDigits: needsDecimals ? 2 : 0,
  })}`;
}

/**
 * Format the billing cadence for display (e.g., "every 30 days", "/month").
 */
export function formatBillingCadence(interval: string, intervalCount: number = 1): string {
  if (intervalCount === 1) {
    return `/${interval}`;
  }
  return ` every ${intervalCount} ${interval}s`;
}

/**
 * Generate the trial terms string shown in the consent checkbox.
 *
 * @param trialDays Number of trial days
 * @param priceCents Price in cents after trial
 * @param interval Billing interval (day, week, month, year)
 * @param intervalCount How many intervals per billing cycle (default 1)
 * @returns Object with trial end date and terms string
 */
export function generateTrialTerms(trialDays: number, priceCents: number, interval: string, intervalCount: number = 1): {
  trialEndDate: Date;
  formattedTrialEnd: string;
  priceDisplay: string;
} {
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + trialDays);

  const cadence = formatBillingCadence(interval, intervalCount);

  return {
    trialEndDate,
    formattedTrialEnd: trialEndDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }),
    priceDisplay: `${formatCurrency(priceCents)}${cadence}`,
  };
}

/** New usage-based pricing constants */
export const PRICING = {
  LIVE_SESSION_RATE_CENTS: 6000,
  LIVE_HOURLY_RATE_CENTS: 14500,
  ON_DEMAND_RATE_CENTS_PER_MIN: 25,
  ON_DEMAND_HOURLY_RATE_CENTS: 1500,
  TRIAL_DAYS: 14,
  TRIAL_FREE_SESSIONS: 1,
  TRIAL_OD_HOURS_PER_DAY: 1,
} as const;

export const FAMILY_FAVORITES = [
  {
    name: 'Light Practice',
    description: '1 Live session per month and 5 minutes of On-Demand work most days',
    liveSessions: 1,
    odMinutesPerDay: 5,
    odDays: 20,
    monthlyCents: 8500,
  },
  {
    name: 'Dedicated Practice',
    description: '1 Live session per month and 15 minutes of On-Demand work most days',
    liveSessions: 1,
    odMinutesPerDay: 15,
    odDays: 25,
    monthlyCents: 17500,
  },
  {
    name: 'Transformational Practice',
    description: '2 Live sessions per month and 25 minutes of On-Demand work most days',
    liveSessions: 2,
    odMinutesPerDay: 25,
    odDays: 25,
    monthlyCents: 31000,
  },
] as const;

/** Calculate estimated monthly cost from planned usage */
export function calculateMonthlyEstimate(liveSessions: number, totalOdMinutes: number): number {
  return liveSessions * PRICING.LIVE_SESSION_RATE_CENTS + totalOdMinutes * PRICING.ON_DEMAND_RATE_CENTS_PER_MIN;
}

/** Calculate the complimentary trial value */
export function calculateTrialValue(): { liveValueCents: number; odValueCents: number; totalValueCents: number } {
  const liveValueCents = PRICING.TRIAL_FREE_SESSIONS * PRICING.LIVE_SESSION_RATE_CENTS;
  const odHours = PRICING.TRIAL_DAYS * PRICING.TRIAL_OD_HOURS_PER_DAY;
  const odValueCents = odHours * PRICING.ON_DEMAND_HOURLY_RATE_CENTS;
  return { liveValueCents, odValueCents, totalValueCents: liveValueCents + odValueCents };
}

/** Format total minutes as hours with one decimal (e.g. 312 → "5.2") */
export function formatOdHours(totalMinutes: number): string {
  return (totalMinutes / 60).toFixed(1);
}

/** Get calendar grid data for a given month (0-indexed). Returns day numbers with day-of-week (0=Sun). */
export function getCalendarDays(year: number, month: number): { day: number; dayOfWeek: number }[] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const result: { day: number; dayOfWeek: number }[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    result.push({ day: d, dayOfWeek: new Date(year, month, d).getDay() });
  }
  return result;
}

export interface DayPlan { live: number; odMinutes: number }

/** Sum planned usage across all days and return total cost in cents */
export function calculatePlannerTotal(days: Record<number, DayPlan>): number {
  let totalLive = 0;
  let totalOdMinutes = 0;
  for (const plan of Object.values(days)) {
    totalLive += plan.live;
    totalOdMinutes += plan.odMinutes;
  }
  return calculateMonthlyEstimate(totalLive, totalOdMinutes);
}
